/**
 * Market Comparables Page - Phase 5
 * Investment intelligence module for comparable analysis
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  House,
  MagnifyingGlass,
  FunnelSimple,
  ChartLine,
  TrendUp,
  TrendDown,
  CheckCircle,
  Warning,
  ShieldCheck,
  ShieldWarning,
  Eye,
  EyeSlash,
  Plus,
  CaretDown,
  X,
  Target,
  CurrencyEur,
  MapPin,
  Clock,
  ArrowsClockwise,
  PencilSimple,
} from '@phosphor-icons/react';
import { LoadingPage, EmptyState } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { comparablesApi, dealsApi } from '../utils/api';
import {
  formatCurrency,
  formatPercent,
  formatArea,
  formatPricePerSqm,
  formatDate,
  getRoiColorClass,
} from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Constants
const OBSERVATION_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'sold', label: 'Sold Only' },
  { value: 'asking', label: 'Asking Only' },
  { value: 'estimated', label: 'Estimated' },
];

const RENOVATION_STATES = [
  { value: 'all', label: 'All States' },
  { value: 'poor', label: 'Poor' },
  { value: 'dated', label: 'Dated' },
  { value: 'average', label: 'Average' },
  { value: 'good', label: 'Good' },
  { value: 'renovated', label: 'Renovated' },
  { value: 'premium_renovated', label: 'Premium Renovated' },
];

const ARRONDISSEMENTS = [
  '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
  '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th'
];

const ObservationBadge = ({ type }) => {
  const config = {
    sold: { color: 'bg-emerald-100 text-emerald-700', label: 'Sold' },
    asking: { color: 'bg-amber-100 text-amber-700', label: 'Asking' },
    estimated: { color: 'bg-blue-100 text-blue-700', label: 'Estimated' },
  };
  const c = config[type] || config.asking;
  return <Badge variant="secondary" className={cn('text-xs', c.color)}>{c.label}</Badge>;
};

const RenovationBadge = ({ state }) => {
  const config = {
    poor: { color: 'bg-red-100 text-red-700' },
    dated: { color: 'bg-orange-100 text-orange-700' },
    average: { color: 'bg-stone-100 text-stone-700' },
    good: { color: 'bg-lime-100 text-lime-700' },
    renovated: { color: 'bg-emerald-100 text-emerald-700' },
    premium_renovated: { color: 'bg-cyan-100 text-cyan-700' },
  };
  const c = config[state] || config.average;
  const label = state?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  return <Badge variant="secondary" className={cn('text-xs capitalize', c.color)}>{label}</Badge>;
};

const ConfidenceBadge = ({ level }) => {
  const config = {
    high: { color: 'text-emerald-600', icon: ShieldCheck, label: 'High' },
    medium: { color: 'text-amber-600', icon: Warning, label: 'Medium' },
    low: { color: 'text-red-600', icon: ShieldWarning, label: 'Low' },
  };
  const c = config[level] || config.medium;
  const Icon = c.icon;
  return (
    <span className={cn('flex items-center gap-1 text-xs', c.color)}>
      <Icon size={14} weight="fill" />
      {c.label}
    </span>
  );
};

const RelevanceScore = ({ score }) => {
  let color = 'text-red-600';
  let label = 'Poor';
  if (score >= 80) {
    color = 'text-emerald-600';
    label = 'Excellent';
  } else if (score >= 65) {
    color = 'text-lime-600';
    label = 'Good';
  } else if (score >= 50) {
    color = 'text-amber-600';
    label = 'Fair';
  }
  
  return (
    <div className="text-center">
      <p className={cn('text-lg font-bold', color)}>{score?.toFixed(0) || '—'}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
};

export const MarketComparables = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dealIdFromUrl = searchParams.get('deal');
  const actionFromUrl = searchParams.get('action');
  
  const [loading, setLoading] = useState(true);
  const [comparables, setComparables] = useState([]);
  const [deals, setDeals] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [dealAnalysis, setDealAnalysis] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(dealIdFromUrl || 'all');
  const [selectedComp, setSelectedComp] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Add/Edit Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [formData, setFormData] = useState({
    address: '',
    arrondissement: '',
    postal_code: '',
    surface_sqm: '',
    rooms: '',
    floor: '',
    property_type: 'apartment',
    building_type: 'haussmann',
    observation_type: 'asking',
    renovation_state: 'average',
    asking_price: '',
    sold_price: '',
    date_observed: new Date().toISOString().split('T')[0],
    source_platform: 'Manual Entry',
    has_elevator: false,
    has_balcony: false,
    notes: '',
    deal_id: '',
  });
  const [saving, setSaving] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [observationType, setObservationType] = useState('all');
  const [renovationState, setRenovationState] = useState('all');
  const [arrondissement, setArrondissement] = useState('all');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Handle action URL param
  useEffect(() => {
    if (actionFromUrl === 'add') {
      setShowAddModal(true);
      // Clear the action param from URL
      setSearchParams(prev => {
        prev.delete('action');
        return prev;
      });
    }
  }, [actionFromUrl, setSearchParams]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters
      const filters = {
        exclude_duplicates: true,
      };
      if (selectedDeal !== 'all') {
        filters.deal_id = selectedDeal;
      }
      if (observationType !== 'all') {
        filters.observation_type = observationType;
      }
      if (renovationState !== 'all') {
        filters.renovation_state = renovationState;
      }
      if (arrondissement !== 'all') {
        filters.arrondissement = arrondissement;
      }
      if (verifiedOnly) {
        filters.verified_only = true;
      }
      
      const [compsData, dealsData, statsData] = await Promise.all([
        comparablesApi.getAll(filters),
        dealsApi.getAll(),
        comparablesApi.getStatistics(arrondissement !== 'all' ? arrondissement : null),
      ]);
      
      setComparables(compsData);
      setDeals(dealsData);
      setStatistics(statsData);
      
      // Load deal analysis if a deal is selected
      if (selectedDeal && selectedDeal !== 'all') {
        const analysis = await comparablesApi.getDealAnalysis(selectedDeal);
        setDealAnalysis(analysis);
      } else {
        setDealAnalysis(null);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDeal, observationType, renovationState, arrondissement, verifiedOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter comparables by search
  const filteredComparables = useMemo(() => {
    if (!searchQuery) return comparables;
    const query = searchQuery.toLowerCase();
    return comparables.filter(c =>
      c.address?.toLowerCase().includes(query) ||
      c.label?.toLowerCase().includes(query) ||
      c.source_platform?.toLowerCase().includes(query)
    );
  }, [comparables, searchQuery]);

  // Get scored comps from analysis
  const scoredComps = useMemo(() => {
    if (!dealAnalysis?.top_comps) return [];
    return dealAnalysis.top_comps;
  }, [dealAnalysis]);

  // Open detail modal
  const openCompDetails = (comp) => {
    setSelectedComp(comp);
    setShowDetails(true);
  };

  // ============== CRUD HANDLERS ==============
  
  // Reset form
  const resetForm = () => {
    setFormData({
      address: '',
      arrondissement: '',
      postal_code: '',
      surface_sqm: '',
      rooms: '',
      floor: '',
      property_type: 'apartment',
      building_type: 'haussmann',
      observation_type: 'asking',
      renovation_state: 'average',
      asking_price: '',
      sold_price: '',
      date_observed: new Date().toISOString().split('T')[0],
      source_platform: 'Manual Entry',
      has_elevator: false,
      has_balcony: false,
      notes: '',
      deal_id: selectedDeal !== 'all' ? selectedDeal : '',
    });
  };

  // Open Add Modal
  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  // Open Edit Modal
  const openEditModal = (comp) => {
    setEditingComp(comp);
    setFormData({
      address: comp.address || '',
      arrondissement: comp.arrondissement || '',
      postal_code: comp.postal_code || '',
      surface_sqm: comp.surface_sqm?.toString() || '',
      rooms: comp.rooms?.toString() || '',
      floor: comp.floor?.toString() || '',
      property_type: comp.property_type || 'apartment',
      building_type: comp.building_type || 'haussmann',
      observation_type: comp.observation_type || 'asking',
      renovation_state: comp.renovation_state || 'average',
      asking_price: comp.asking_price?.toString() || '',
      sold_price: comp.sold_price?.toString() || '',
      date_observed: comp.date_observed?.split('T')[0] || new Date().toISOString().split('T')[0],
      source_platform: comp.source_platform || 'Manual Entry',
      has_elevator: comp.has_elevator || false,
      has_balcony: comp.has_balcony || false,
      notes: comp.notes || '',
      deal_id: comp.deal_id || '',
    });
    setShowEditModal(true);
  };

  // Handle form field change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Submit Add Comparable
  const handleAddSubmit = async () => {
    try {
      setSaving(true);
      
      const payload = {
        address: formData.address,
        arrondissement: formData.arrondissement,
        postal_code: formData.postal_code,
        surface_sqm: parseFloat(formData.surface_sqm) || 0,
        rooms: parseInt(formData.rooms) || null,
        floor: parseInt(formData.floor) || null,
        property_type: formData.property_type,
        building_type: formData.building_type,
        observation_type: formData.observation_type,
        renovation_state: formData.renovation_state,
        asking_price: parseFloat(formData.asking_price) || null,
        sold_price: parseFloat(formData.sold_price) || null,
        date_observed: formData.date_observed,
        source_platform: formData.source_platform,
        has_elevator: formData.has_elevator,
        has_balcony: formData.has_balcony,
        notes: formData.notes || null,
        deal_id: formData.deal_id || null,
      };
      
      await comparablesApi.create(payload);
      toast.success('Comparable ajouté');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to add comparable:', err);
      toast.error(err.response?.data?.detail?.validation_errors?.join(', ') || 'Échec de l\'ajout');
    } finally {
      setSaving(false);
    }
  };

  // Submit Edit Comparable
  const handleEditSubmit = async () => {
    if (!editingComp) return;
    
    try {
      setSaving(true);
      
      const payload = {
        address: formData.address,
        arrondissement: formData.arrondissement,
        postal_code: formData.postal_code,
        surface_sqm: parseFloat(formData.surface_sqm) || 0,
        rooms: parseInt(formData.rooms) || null,
        floor: parseInt(formData.floor) || null,
        property_type: formData.property_type,
        building_type: formData.building_type,
        observation_type: formData.observation_type,
        renovation_state: formData.renovation_state,
        asking_price: parseFloat(formData.asking_price) || null,
        sold_price: parseFloat(formData.sold_price) || null,
        date_observed: formData.date_observed,
        source_platform: formData.source_platform,
        has_elevator: formData.has_elevator,
        has_balcony: formData.has_balcony,
        notes: formData.notes || null,
        deal_id: formData.deal_id || null,
      };
      
      await comparablesApi.update(editingComp.id, payload);
      toast.success('Comparable mis à jour');
      setShowEditModal(false);
      setEditingComp(null);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to update comparable:', err);
      toast.error('Échec de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Verify Comparable
  const handleVerify = async (compId) => {
    try {
      await comparablesApi.verify(compId);
      toast.success('Comparable vérifié');
      fetchData();
    } catch (err) {
      toast.error('Échec de la vérification');
    }
  };

  // Exclude Comparable
  const handleExclude = async (compId) => {
    try {
      await comparablesApi.exclude(compId, 'Manually excluded');
      toast.success('Comparable exclu de l\'analyse');
      fetchData();
    } catch (err) {
      toast.error('Échec de l\'exclusion');
    }
  };

  // Include Comparable
  const handleInclude = async (compId) => {
    try {
      await comparablesApi.include(compId);
      toast.success('Comparable inclus dans l\'analyse');
      fetchData();
    } catch (err) {
      toast.error('Échec de l\'inclusion');
    }
  };

  // Delete Comparable
  const handleDelete = async (compId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce comparable ?')) return;
    
    try {
      await comparablesApi.delete(compId);
      toast.success('Comparable supprimé');
      setShowDetails(false);
      setSelectedComp(null);
      fetchData();
    } catch (err) {
      toast.error('Échec de la suppression');
    }
  };

  if (loading) return <LoadingPage message="Chargement des comparables..." />;

  return (
    <div data-testid="market-comparables" className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <House size={24} className="sm:hidden" />
            <House size={28} className="hidden sm:block" />
            Comparables de Marché
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Analyser les données du marché pour les décisions de prix
          </p>
        </div>
        <Button className="bg-[#3A4A40] hover:bg-[#2E3B33] w-full sm:w-auto" data-testid="add-comparable-btn" onClick={openAddModal}>
          <Plus size={18} className="mr-1" />
          Ajouter un comparable
        </Button>
      </div>

      {/* Statistics Summary - Mobile responsive */}
      {statistics && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4" data-testid="comp-statistics">
          <Card>
            <CardContent className="p-3 sm:pt-4 text-center">
              <p className="text-lg sm:text-2xl font-bold font-tabular">{statistics.total_count}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-4 text-center">
              <p className="text-lg sm:text-2xl font-bold font-tabular text-emerald-600">
                {statistics.by_observation_type?.sold || 0}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Vendu</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 sm:pt-4 text-center">
              <p className="text-lg sm:text-2xl font-bold font-tabular text-amber-600">
                {statistics.by_observation_type?.asking || 0}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Demandé</p>
            </CardContent>
          </Card>
          <Card className="hidden sm:block">
            <CardContent className="p-3 sm:pt-4 text-center">
              <p className="text-lg sm:text-2xl font-bold font-tabular">
                {formatPricePerSqm(statistics.price_range?.median)}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Médiane €/m²</p>
            </CardContent>
          </Card>
          <Card className="col-span-2 sm:col-span-1">
            <CardContent className="p-3 sm:pt-4 text-center">
              <p className="text-sm sm:text-lg font-bold font-tabular">
                {formatPricePerSqm(statistics.price_range?.min)} - {formatPricePerSqm(statistics.price_range?.max)}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Fourchette de prix</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Bar - Mobile responsive */}
      <Card data-testid="filter-bar">
        <CardContent className="p-3 sm:pt-4">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Primary row */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
              {/* Search */}
              <div className="relative flex-1 sm:flex-none">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="comp-search"
                  placeholder="Search comparables..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-56"
                />
              </div>

              {/* Deal Filter */}
              <Select value={selectedDeal} onValueChange={setSelectedDeal}>
                <SelectTrigger data-testid="deal-filter" className="w-full sm:w-56">
                  <SelectValue placeholder="Select Deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Comparables</SelectItem>
                  {deals.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.deal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                {/* Observation Type */}
                <Select value={observationType} onValueChange={setObservationType}>
                  <SelectTrigger data-testid="obs-type-filter" className="flex-1 sm:w-32">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {OBSERVATION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* More Filters Toggle */}
                <Button
                  variant={showFilters ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="h-10"
                >
                  <FunnelSimple size={16} className="sm:mr-1" />
                  <span className="hidden sm:inline">Filters</span>
                  <CaretDown size={14} className={cn("ml-1 transition-transform hidden sm:block", showFilters && "rotate-180")} />
                </Button>

                {/* Refresh */}
                <Button variant="ghost" size="sm" onClick={fetchData} className="h-10 px-2">
                  <ArrowsClockwise size={16} />
                </Button>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-3 border-t animate-fade-in">
                <div className="flex flex-col gap-1 flex-1 sm:flex-none">
                  <label className="text-xs text-muted-foreground">Arrondissement</label>
                  <Select value={arrondissement} onValueChange={setArrondissement}>
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {ARRONDISSEMENTS.map(a => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1 flex-1 sm:flex-none">
                  <label className="text-xs text-muted-foreground">Condition</label>
                  <Select value={renovationState} onValueChange={setRenovationState}>
                    <SelectTrigger className="w-full sm:w-44">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      {RENOVATION_STATES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 pt-4 sm:pt-5">
                  <input
                    type="checkbox"
                    id="verified-only"
                    checked={verifiedOnly}
                    onChange={(e) => setVerifiedOnly(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="verified-only" className="text-sm">Verified only</label>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Deal Analysis Panel (when deal selected) */}
      {dealAnalysis && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="deal-analysis">
          {/* Market Ranges */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <ChartLine size={18} />
                Current Condition Range
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Low</span>
                  <span className="font-tabular">{formatPricePerSqm(dealAnalysis.current_condition_range?.per_sqm_low)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium">
                  <span>Base</span>
                  <span className="font-tabular">{formatPricePerSqm(dealAnalysis.current_condition_range?.per_sqm_base)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">High</span>
                  <span className="font-tabular">{formatPricePerSqm(dealAnalysis.current_condition_range?.per_sqm_high)}</span>
                </div>
                <hr className="my-2" />
                <p className="text-xs text-muted-foreground">
                  Based on {dealAnalysis.current_condition_range?.comp_count} comps
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendUp size={18} />
                Renovated Exit Range
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Low</span>
                  <span className="font-tabular">{formatPricePerSqm(dealAnalysis.renovated_exit_range?.per_sqm_low)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium">
                  <span>Base</span>
                  <span className="font-tabular">{formatPricePerSqm(dealAnalysis.renovated_exit_range?.per_sqm_base)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">High</span>
                  <span className="font-tabular">{formatPricePerSqm(dealAnalysis.renovated_exit_range?.per_sqm_high)}</span>
                </div>
                <hr className="my-2" />
                <p className="text-xs text-muted-foreground">
                  Based on {dealAnalysis.renovated_exit_range?.comp_count} renovated comps
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recommendation */}
          <Card className={cn(
            "border-2",
            dealAnalysis.recommendation?.recommendation_type === 'strong_buy' && "border-emerald-300 bg-emerald-50/30",
            dealAnalysis.recommendation?.recommendation_type === 'overpriced' && "border-red-300 bg-red-50/30",
            dealAnalysis.recommendation?.recommendation_type === 'exit_aggressive' && "border-amber-300 bg-amber-50/30",
            dealAnalysis.recommendation?.recommendation_type === 'weak_comps' && "border-stone-300 bg-stone-50/30"
          )}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target size={18} />
                Recommendation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold">{dealAnalysis.recommendation?.headline}</p>
              <p className="text-sm text-muted-foreground mt-1">{dealAnalysis.recommendation?.details}</p>
              <div className="mt-3 pt-3 border-t space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Max Acquisition</span>
                  <span className="font-tabular font-medium">{formatCurrency(dealAnalysis.recommendation?.max_acquisition_supported)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Confidence</span>
                  <Badge variant="secondary" className="capitalize">{dealAnalysis.recommendation?.confidence_level}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gap Analysis Row (when deal selected) */}
      {dealAnalysis && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Acquisition Gap */}
          <Card data-testid="acquisition-gap">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Acquisition Gap Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Target Acquisition</p>
                  <p className="text-lg font-bold font-tabular">{formatCurrency(dealAnalysis.acquisition_gap?.target_acquisition_price)}</p>
                  <p className="text-xs text-muted-foreground">{formatPricePerSqm(dealAnalysis.acquisition_gap?.target_acquisition_per_sqm)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Market Value</p>
                  <p className="text-lg font-bold font-tabular">{formatCurrency(dealAnalysis.acquisition_gap?.market_value_weighted)}</p>
                  <p className="text-xs text-muted-foreground">{formatPricePerSqm(dealAnalysis.acquisition_gap?.market_value_per_sqm)}</p>
                </div>
              </div>
              <div className={cn(
                "mt-4 p-3 rounded-lg text-center",
                dealAnalysis.acquisition_gap?.discount_percent > 10 && "bg-emerald-100",
                dealAnalysis.acquisition_gap?.discount_percent > 0 && dealAnalysis.acquisition_gap?.discount_percent <= 10 && "bg-lime-100",
                dealAnalysis.acquisition_gap?.discount_percent <= 0 && dealAnalysis.acquisition_gap?.discount_percent > -10 && "bg-amber-100",
                dealAnalysis.acquisition_gap?.discount_percent <= -10 && "bg-red-100"
              )}>
                <p className={cn(
                  "text-xl font-bold",
                  dealAnalysis.acquisition_gap?.discount_percent > 0 ? "text-emerald-700" : "text-red-700"
                )}>
                  {dealAnalysis.acquisition_gap?.discount_percent > 0 ? '+' : ''}{dealAnalysis.acquisition_gap?.discount_percent?.toFixed(1)}%
                </p>
                <p className="text-sm capitalize">{dealAnalysis.acquisition_gap?.gap_label?.replace('_', ' ')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Exit Realism */}
          <Card data-testid="exit-realism">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Exit Realism Check</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Assumed Exit</p>
                  <p className="text-lg font-bold font-tabular">{formatPricePerSqm(dealAnalysis.exit_realism?.assumed_exit_per_sqm)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Comp-Supported</p>
                  <p className="text-lg font-bold font-tabular">{formatPricePerSqm(dealAnalysis.exit_realism?.comp_supported_per_sqm)}</p>
                </div>
              </div>
              <div className={cn(
                "mt-4 p-3 rounded-lg text-center",
                dealAnalysis.exit_realism?.realism_label === 'conservative' && "bg-cyan-100",
                dealAnalysis.exit_realism?.realism_label === 'realistic' && "bg-emerald-100",
                dealAnalysis.exit_realism?.realism_label === 'optimistic' && "bg-amber-100",
                dealAnalysis.exit_realism?.realism_label === 'over_optimistic' && "bg-red-100"
              )}>
                <p className={cn(
                  "text-xl font-bold capitalize",
                  dealAnalysis.exit_realism?.realism_label === 'realistic' && "text-emerald-700",
                  dealAnalysis.exit_realism?.realism_label === 'conservative' && "text-cyan-700",
                  dealAnalysis.exit_realism?.realism_label === 'optimistic' && "text-amber-700",
                  dealAnalysis.exit_realism?.realism_label === 'over_optimistic' && "text-red-700"
                )}>
                  {dealAnalysis.exit_realism?.realism_label?.replace('_', ' ')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {dealAnalysis.exit_realism?.delta_percent > 0 ? '+' : ''}{dealAnalysis.exit_realism?.delta_percent?.toFixed(1)}% vs base
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Comp Set Strength (when deal selected) */}
      {dealAnalysis?.comp_set_strength && (
        <Card data-testid="comp-set-strength">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Comparable Set Strength</CardTitle>
              <Badge 
                variant="secondary" 
                className={cn(
                  "text-sm",
                  dealAnalysis.comp_set_strength.overall_strength === 'strong' && "bg-emerald-100 text-emerald-700",
                  dealAnalysis.comp_set_strength.overall_strength === 'medium' && "bg-amber-100 text-amber-700",
                  dealAnalysis.comp_set_strength.overall_strength === 'weak' && "bg-red-100 text-red-700"
                )}
              >
                {dealAnalysis.comp_set_strength.overall_strength?.toUpperCase()} ({dealAnalysis.comp_set_strength.score})
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold">{dealAnalysis.comp_set_strength.total_comp_count}</p>
                <p className="text-xs text-muted-foreground">Total Comps</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{dealAnalysis.comp_set_strength.good_comp_count}</p>
                <p className="text-xs text-muted-foreground">Good Quality</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{dealAnalysis.comp_set_strength.sold_count}</p>
                <p className="text-xs text-muted-foreground">Sold Data</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{dealAnalysis.comp_set_strength.average_recency_days}</p>
                <p className="text-xs text-muted-foreground">Avg Days Old</p>
              </div>
              <div>
                <p className="text-2xl font-bold capitalize">{dealAnalysis.comp_set_strength.geographic_concentration}</p>
                <p className="text-xs text-muted-foreground">Concentration</p>
              </div>
            </div>
            {dealAnalysis.comp_set_strength.weaknesses?.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                <p className="text-sm font-medium text-amber-800">Weaknesses:</p>
                <ul className="text-sm text-amber-700 mt-1">
                  {dealAnalysis.comp_set_strength.weaknesses.map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comparables Table */}
      <Card data-testid="comparables-table">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Comparables ({filteredComparables.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredComparables.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={House}
                title="No comparables found"
                description="Essayez d'ajuster vos filtres ou ajoutez de nouveaux comparables."
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/30">
                  <TableHead>Address</TableHead>
                  <TableHead>Arr.</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">€/m²</TableHead>
                  <TableHead className="text-center">Confidence</TableHead>
                  <TableHead className="text-center">Verified</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredComparables.map((comp) => {
                  const effectivePrice = comp.sold_price || comp.asking_price || comp.estimated_value;
                  const pricePerSqm = comp.price_per_sqm || (effectivePrice && comp.surface_sqm > 0 ? effectivePrice / comp.surface_sqm : null);
                  
                  return (
                    <TableRow 
                      key={comp.id}
                      className={cn(
                        "hover:bg-secondary/20 cursor-pointer transition-colors",
                        comp.excluded_from_analysis && "opacity-50 bg-stone-50"
                      )}
                      onClick={() => openCompDetails(comp)}
                      data-testid={`comp-row-${comp.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{comp.label || comp.address}</p>
                          {comp.label && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{comp.address}</p>}
                        </div>
                      </TableCell>
                      <TableCell>{comp.arrondissement}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-sm">{comp.source_platform}</span>
                          {comp.source_import_id && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-violet-50 text-violet-600 border-violet-200">
                              Import
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell><ObservationBadge type={comp.observation_type} /></TableCell>
                      <TableCell className="text-sm">{formatDate(comp.date_observed)}</TableCell>
                      <TableCell className="text-right font-tabular">{formatArea(comp.surface_sqm)}</TableCell>
                      <TableCell><RenovationBadge state={comp.renovation_state} /></TableCell>
                      <TableCell className="text-right font-tabular font-medium">
                        {formatCurrency(effectivePrice)}
                      </TableCell>
                      <TableCell className="text-right font-tabular">
                        {formatPricePerSqm(pricePerSqm)}
                      </TableCell>
                      <TableCell className="text-center">
                        <ConfidenceBadge level={comp.confidence_level} />
                      </TableCell>
                      <TableCell className="text-center">
                        {comp.manually_verified ? (
                          <CheckCircle size={18} weight="fill" className="text-emerald-600 mx-auto" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Comparable Detail Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          {selectedComp && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <House size={24} />
                  {selectedComp.label || selectedComp.address}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Location */}
                <div className="flex items-start gap-2">
                  <MapPin size={18} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p>{selectedComp.address}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedComp.arrondissement} Arr. • {selectedComp.postal_code}
                      {selectedComp.distance_meters && ` • ${selectedComp.distance_meters}m from subject`}
                    </p>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Type</p>
                    <ObservationBadge type={selectedComp.observation_type} />
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Surface</p>
                    <p className="text-lg font-bold font-tabular">{formatArea(selectedComp.surface_sqm)}</p>
                  </div>
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground uppercase">Rooms</p>
                    <p className="text-lg font-bold">{selectedComp.rooms || '—'}</p>
                  </div>
                </div>

                {/* Prices */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedComp.asking_price && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground uppercase">Asking Price</p>
                      <p className="text-xl font-bold font-tabular">{formatCurrency(selectedComp.asking_price)}</p>
                    </div>
                  )}
                  {selectedComp.sold_price && (
                    <div className="p-3 border rounded-lg border-emerald-200 bg-emerald-50/30">
                      <p className="text-xs text-muted-foreground uppercase">Sold Price</p>
                      <p className="text-xl font-bold font-tabular text-emerald-700">{formatCurrency(selectedComp.sold_price)}</p>
                    </div>
                  )}
                </div>

                {/* Property Details */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Floor</span>
                    <span>{selectedComp.floor || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Building Type</span>
                    <span className="capitalize">{selectedComp.building_type || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Condition</span>
                    <span className="capitalize">{selectedComp.apartment_condition || selectedComp.condition || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Renovation</span>
                    <RenovationBadge state={selectedComp.renovation_state} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Elevator</span>
                    <span>{selectedComp.has_elevator ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Balcony</span>
                    <span>{selectedComp.has_balcony ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Source & Quality */}
                <div className="p-3 bg-secondary/30 rounded-lg space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Source</span>
                    <span>{selectedComp.source_platform}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Observed</span>
                    <span>{formatDate(selectedComp.date_observed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Confidence</span>
                    <ConfidenceBadge level={selectedComp.confidence_level} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified</span>
                    <span>{selectedComp.manually_verified ? `Yes (${selectedComp.verified_by})` : 'No'}</span>
                  </div>
                </div>

                {/* Notes */}
                {selectedComp.notes && (
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Notes</p>
                    <p className="text-sm">{selectedComp.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowDetails(false);
                      openEditModal(selectedComp);
                    }}
                    data-testid="edit-comp-btn"
                  >
                    <PencilSimple size={16} className="mr-1" />
                    Edit
                  </Button>
                  {!selectedComp.manually_verified && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleVerify(selectedComp.id)}
                      data-testid="verify-comp-btn"
                    >
                      <CheckCircle size={16} className="mr-1" />
                      Verify
                    </Button>
                  )}
                  {selectedComp.manually_verified && (
                    <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                      <CheckCircle size={14} weight="fill" />
                      Verified
                    </Badge>
                  )}
                  {!selectedComp.excluded_from_analysis ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleExclude(selectedComp.id)}
                      data-testid="exclude-comp-btn"
                    >
                      <EyeSlash size={16} className="mr-1" />
                      Exclure
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleInclude(selectedComp.id)}
                      data-testid="include-comp-btn"
                    >
                      <Eye size={16} className="mr-1" />
                      Inclure
                    </Button>
                  )}
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDelete(selectedComp.id)}
                    data-testid="delete-comp-btn"
                  >
                    <X size={16} className="mr-1" />
                    Supprimer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Comparable Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={20} />
              Add New Comparable
            </DialogTitle>
          </DialogHeader>
          <ComparableForm 
            formData={formData}
            onChange={handleFormChange}
            onSubmit={handleAddSubmit}
            onCancel={() => setShowAddModal(false)}
            saving={saving}
            deals={deals}
            isEdit={false}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Comparable Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={20} />
              Edit Comparable
            </DialogTitle>
          </DialogHeader>
          <ComparableForm 
            formData={formData}
            onChange={handleFormChange}
            onSubmit={handleEditSubmit}
            onCancel={() => {
              setShowEditModal(false);
              setEditingComp(null);
            }}
            saving={saving}
            deals={deals}
            isEdit={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== COMPARABLE FORM COMPONENT ==============
const ComparableForm = ({ formData, onChange, onSubmit, onCancel, saving, deals, isEdit }) => {
  return (
    <div className="space-y-4">
      {/* Address Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Address *</label>
          <Input
            value={formData.address}
            onChange={(e) => onChange('address', e.target.value)}
            placeholder="123 Rue de Example"
            data-testid="comp-form-address"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Arrondissement *</label>
          <Select value={formData.arrondissement} onValueChange={(v) => onChange('arrondissement', v)}>
            <SelectTrigger data-testid="comp-form-arrondissement">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {ARRONDISSEMENTS.map(a => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Postal + Surface Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Postal Code</label>
          <Input
            value={formData.postal_code}
            onChange={(e) => onChange('postal_code', e.target.value)}
            placeholder="75001"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Surface (m²) *</label>
          <Input
            type="number"
            value={formData.surface_sqm}
            onChange={(e) => onChange('surface_sqm', e.target.value)}
            placeholder="65"
            data-testid="comp-form-surface"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Rooms</label>
          <Input
            type="number"
            value={formData.rooms}
            onChange={(e) => onChange('rooms', e.target.value)}
            placeholder="3"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Floor</label>
          <Input
            type="number"
            value={formData.floor}
            onChange={(e) => onChange('floor', e.target.value)}
            placeholder="2"
          />
        </div>
      </div>

      {/* Property Details Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Property Type</label>
          <Select value={formData.property_type} onValueChange={(v) => onChange('property_type', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="apartment">Apartment</SelectItem>
              <SelectItem value="studio">Studio</SelectItem>
              <SelectItem value="loft">Loft</SelectItem>
              <SelectItem value="house">House</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Building Type</label>
          <Select value={formData.building_type} onValueChange={(v) => onChange('building_type', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="haussmann">Haussmann</SelectItem>
              <SelectItem value="pierre_de_taille">Pierre de Taille</SelectItem>
              <SelectItem value="modern">Modern</SelectItem>
              <SelectItem value="art_deco">Art Deco</SelectItem>
              <SelectItem value="post_war">Post War</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formData.has_elevator}
              onChange={(e) => onChange('has_elevator', e.target.checked)}
              className="rounded"
            />
            Elevator
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formData.has_balcony}
              onChange={(e) => onChange('has_balcony', e.target.checked)}
              className="rounded"
            />
            Balcony
          </label>
        </div>
      </div>

      {/* Observation & Condition Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Observation Type *</label>
          <Select value={formData.observation_type} onValueChange={(v) => onChange('observation_type', v)}>
            <SelectTrigger data-testid="comp-form-obs-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asking">Asking</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
              <SelectItem value="estimated">Estimated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Renovation State</label>
          <Select value={formData.renovation_state} onValueChange={(v) => onChange('renovation_state', v)}>
            <SelectTrigger data-testid="comp-form-reno-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RENOVATION_STATES.filter(s => s.value !== 'all').map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Date Observed</label>
          <Input
            type="date"
            value={formData.date_observed}
            onChange={(e) => onChange('date_observed', e.target.value)}
          />
        </div>
      </div>

      {/* Price Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Asking Price (€) {formData.observation_type === 'asking' && '*'}
          </label>
          <Input
            type="number"
            value={formData.asking_price}
            onChange={(e) => onChange('asking_price', e.target.value)}
            placeholder="650000"
            data-testid="comp-form-asking-price"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Sold Price (€) {formData.observation_type === 'sold' && '*'}
          </label>
          <Input
            type="number"
            value={formData.sold_price}
            onChange={(e) => onChange('sold_price', e.target.value)}
            placeholder="620000"
            data-testid="comp-form-sold-price"
          />
        </div>
      </div>

      {/* Source & Deal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Source Platform</label>
          <Input
            value={formData.source_platform}
            onChange={(e) => onChange('source_platform', e.target.value)}
            placeholder="SeLoger, PAP, etc."
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Assign to Deal (optional)</label>
          <Select value={formData.deal_id || 'none'} onValueChange={(v) => onChange('deal_id', v === 'none' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select deal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Deal (Global)</SelectItem>
              {deals.map(d => (
                <SelectItem key={d.id} value={d.id}>{d.deal_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          placeholder="Additional notes about this comparable..."
          className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Annuler
        </Button>
        <Button 
          onClick={onSubmit} 
          disabled={saving || !formData.address || !formData.arrondissement || !formData.surface_sqm}
          className="bg-[#3A4A40] hover:bg-[#2E3B33]"
          data-testid="comp-form-submit"
        >
          {saving ? (
            <>
              <ArrowsClockwise size={16} className="mr-1 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <CheckCircle size={16} className="mr-1" />
              {isEdit ? 'Modifier' : 'Ajouter'} comparable
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// Page Help Block
const MarketComparablesWithHelp = () => {
  return (
    <>
      <MarketComparables />
      <PageHelpBlock 
        pageKey="market-comparables" 
        content={pageExplanations.marketComparables} 
      />
    </>
  );
};

export default MarketComparablesWithHelp;
